import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Globe, AlertOctagon, Camera, Info, ExternalLink, Filter } from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/osint/live-world`;

export default function LiveWorld() {
  const { lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [country, setCountry] = useState("");
  const [manufacturer, setManufacturer] = useState("");

  useEffect(() => {
    axios.get(`${API}/status`, { withCredentials: true }).then(r => setStatus(r.data)).catch(() => {});
    axios.get(`${API}/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (country) params.country = country;
    if (manufacturer) params.manufacturer = manufacturer;
    axios.get(`${API}/devices`, { params, withCredentials: true }).then(r => setDevices(r.data.devices || [])).catch(() => setDevices([]));
  }, [country, manufacturer]);

  const maxCount = useMemo(() => {
    if (!stats?.countries?.length) return 1;
    return Math.max(...stats.countries.map(c => c.devices_count));
  }, [stats]);

  const radiusFor = (count) => 4 + Math.round(24 * Math.sqrt(count / maxCount));

  return (
    <div className="app-page-dark min-h-screen bg-[#050505] text-white pb-20" data-testid="live-world-page">
      {/* Header */}
      <div className="border-b border-white/[0.04] bg-[#0A0C10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Globe className="w-5 h-5 text-[#00E5FF]" />
                <h1 className="text-xl font-bold tracking-tight text-white">Live World</h1>
                <span className="text-[9px] tracking-widest uppercase font-bold px-2 py-0.5 bg-[#7C3AED]/12 text-[#B39BFF] border border-[#7C3AED]/30" style={{ borderRadius: "2px" }}>
                  Awareness snapshot
                </span>
              </div>
              <p className="text-[11px] text-white max-w-2xl">
                {lang === "it"
                  ? "Distribuzione globale di dispositivi CCTV/IoT pubblicamente esposti. Solo metadati aggregati — nessun feed video, mai."
                  : "Global distribution of publicly exposed CCTV/IoT devices. Aggregated metadata only — no video feed, ever."}
              </p>
            </div>
            {status && (
              <div className="flex items-center gap-4 text-[10px]">
                <Stat label={lang === "it" ? "Dispositivi indicizzati" : "Devices indexed"} value={status.total_devices_indexed?.toLocaleString("it-IT")} />
                <Stat label="Paesi" value={status.countries_count} />
                <Stat label={lang === "it" ? "Produttori" : "Manufacturers"} value={status.manufacturers_count} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Compliance disclaimer prominent */}
        <div className="bg-[#FF1744]/8 border border-[#FF1744]/30 p-3 flex items-start gap-2" style={{ borderRadius: "2px" }} data-testid="lw-compliance-notice">
          <AlertOctagon className="w-4 h-4 text-[#FF1744] flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-[#FCA5A5]">
            <p className="font-bold text-[#FF1744]">Compliance</p>
            <p>
              {lang === "it"
                ? "Nessun feed video, streaming, iframe o link diretto a telecamere viene mostrato in questa vista. I dati sono uno snapshot aggregato di conteggi già pubblicamente indicizzati da insecam.org — snapshot, non live."
                : "No video feed, stream, iframe or direct camera link is shown here. The data is an aggregated snapshot of counts already publicly indexed by insecam.org — snapshot, not live."}
            </p>
          </div>
        </div>

        {/* World map */}
        <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }} data-testid="lw-map-card">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.04]">
            <Globe className="w-4 h-4 text-[#00E5FF]" />
            <p className="text-[10px] tracking-widest uppercase font-bold text-[#00E5FF]">
              {lang === "it" ? "Distribuzione globale" : "Global distribution"}
            </p>
          </div>
          <div style={{ height: "440px", width: "100%", background: "#050505" }}>
            <MapContainer center={[30, 15]} zoom={2} scrollWheelZoom={false} style={{ height: "100%", width: "100%", background: "#050505" }}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
                attribution='&copy; OSM &copy; CARTO'
              />
              {stats?.countries?.map(c => (
                <CircleMarker
                  key={c.country_code}
                  center={[c.lat, c.lon]}
                  radius={radiusFor(c.devices_count)}
                  pathOptions={{
                    color: c.devices_count > 2000 ? "#FF1744" : c.devices_count > 800 ? "#FFB800" : "#00E5FF",
                    fillColor: c.devices_count > 2000 ? "#FF1744" : c.devices_count > 800 ? "#FFB800" : "#00E5FF",
                    fillOpacity: 0.55, opacity: 0.9, weight: 1,
                  }}
                  eventHandlers={{ click: () => setCountry(c.country_code) }}
                >
                  <Tooltip direction="top" opacity={1}>
                    <div style={{ fontFamily: "monospace", fontSize: "11px" }}>
                      <b>{c.country_name}</b><br />
                      {c.devices_count.toLocaleString("it-IT")} devices
                    </div>
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Filters + Devices table */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Manufacturers panel */}
          <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }} data-testid="lw-manufacturers">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.04]">
              <Camera className="w-4 h-4 text-[#B39BFF]" />
              <p className="text-[10px] tracking-widest uppercase font-bold text-[#B39BFF]">
                {lang === "it" ? "Produttori" : "Manufacturers"}
              </p>
            </div>
            <ul className="divide-y divide-white/[0.04]">
              {stats?.manufacturers?.slice(0, 10).map(m => (
                <li key={m.manufacturer}
                  onClick={() => setManufacturer(m.manufacturer === manufacturer ? "" : m.manufacturer)}
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-white/[0.02] ${manufacturer === m.manufacturer ? "bg-[#B39BFF]/8" : ""}`}
                  data-testid={`lw-manufacturer-${m.manufacturer.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="text-xs text-white font-medium">{m.manufacturer}</span>
                  <span className="text-[11px] tabular-nums text-white">{m.devices_count.toLocaleString("it-IT")}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Devices table */}
          <div className="lg:col-span-2 bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }} data-testid="lw-devices-card">
            <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04]">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#00E5FF]" />
                <p className="text-[10px] tracking-widest uppercase font-bold text-[#00E5FF]">
                  {lang === "it" ? "Dispositivi (campione metadati)" : "Devices (metadata sample)"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {(country || manufacturer) && (
                  <button onClick={() => { setCountry(""); setManufacturer(""); }} className="text-[10px] tracking-widest uppercase text-white hover:text-white">
                    Reset
                  </button>
                )}
                <span className="text-[10px] text-white">{devices.length}</span>
              </div>
            </div>
            {(country || manufacturer) && (
              <div className="px-3 py-1.5 bg-[#00E5FF]/6 border-b border-[#00E5FF]/15 text-[10px] text-[#00E5FF] flex items-center gap-2">
                {country && <span className="uppercase tracking-widest">Paese: {country}</span>}
                {manufacturer && <span className="uppercase tracking-widest">Produttore: {manufacturer}</span>}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="lw-devices-table">
                <thead>
                  <tr className="text-[9px] tracking-widest uppercase text-white border-b border-white/[0.04]">
                    <th className="text-left py-2 px-3">ID</th>
                    <th className="text-left py-2 px-3">{lang === "it" ? "Città" : "City"}</th>
                    <th className="text-left py-2 px-3">Paese</th>
                    <th className="text-left py-2 px-3">Produttore</th>
                    <th className="text-left py-2 px-3">{lang === "it" ? "Modello" : "Model"}</th>
                    <th className="text-right py-2 px-3">Porta</th>
                    <th className="text-right py-2 px-3">{lang === "it" ? "Ultimo rilevamento" : "Last seen"}</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map(d => (
                    <tr key={d.id} className="border-t border-white/[0.03] hover:bg-white/[0.02]" data-testid={`lw-device-${d.id}`}>
                      <td className="py-1.5 px-3 font-mono text-[10px] text-white">{d.id}</td>
                      <td className="py-1.5 px-3 text-white">{d.city}</td>
                      <td className="py-1.5 px-3 text-white">{d.country_name} ({d.country_code})</td>
                      <td className="py-1.5 px-3 text-[#B39BFF]">{d.manufacturer}</td>
                      <td className="py-1.5 px-3 text-white font-mono">{d.model}</td>
                      <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[#00E5FF]">{d.port}</td>
                      <td className="py-1.5 px-3 text-right text-white font-mono">{d.last_seen}</td>
                    </tr>
                  ))}
                  {devices.length === 0 && (
                    <tr><td colSpan={7} className="py-6 text-center text-white italic">{lang === "it" ? "Nessun dispositivo con i filtri correnti." : "No device with current filters."}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* External link with disclaimer */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-3 flex items-start gap-2 text-[10px] text-white" style={{ borderRadius: "2px" }} data-testid="lw-external-note">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{status?.disclaimer}</p>
            <a href="http://www.insecam.org/en/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#00E5FF] hover:text-white mt-1" data-testid="lw-external-link">
              {lang === "it" ? "Sito ufficiale Insecam (esterno)" : "Insecam official site (external)"} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-right">
      <p className="text-[9px] tracking-widest uppercase text-white">{label}</p>
      <p className="text-lg font-bold tabular-nums text-white">{value ?? "—"}</p>
    </div>
  );
}
